/* eslint-disable import/no-duplicates */
import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import { formatPrice, TIPO_USUARIO } from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useClinica } from '../../../hooks/clinica';
import { useAuth } from '../../../hooks/auth';
import { useMedico } from '../../../hooks/medico';

import Input from '../../../components/Input';
import Select from '../../../components/Select';
import SelectSync from '../../../components/SelectSync';
import Loading from '../../../components/Loading';
import Modal from '../../../components/Modal';

import getValidationErrors from '../../../utils/getValidationErrors';

import { arrayGroupBy } from '../../../utils/funcoes';

import getResponseErrors from '../../../utils/getResponseErrors';
import PageInfo from '../../../components/PageInfo';
import { IContrato } from '../../Usuario/Contrato/Cadastro';
import DespesaMedico from './DespesaMedico';
import numeroToExtenso from '../../../utils/numeroPorExtenso';

interface IRelatorio {
  AgeCodigo: number;
  AgeTipo: string;
  AgeInicio: Date;
  AgeInicioFormatado: string;
  AgeStatus: number;
  AgeClinica: number;
  PacNome: string;
  Procedimento: string;
  ConCodigo: number;
  ConNome: string;
  ConUsuCodigo: number;
  PerCodigo: number;
  marcado: boolean;
  ProValor: number;
  CprValor: number;
  CprValorPag: number;
  podePagar: boolean;
}

interface IRetornoContratoEAgendamentosMedico {
  agendamentos: IRelatorio[];
  contrato: IContrato | undefined;
}

interface IRelatorioFormatado {
  grupo: string;
  totalRegistros: number;
  dados: IRelatorio[];
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  CliCodigo: string;
  PerCodigo: string;
  ConUsuCodigo: string;
  ProCodigo: string;
}

const PagamentoMedico: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsClinica } = useClinica();

  const { optionsMedico, buscaOptionsMedico } = useMedico();

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [showModalAddPagamento, setShowModalAddPagamento] = useState(false);

  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorioFormatado[]>();
  const [contrato, setContrato] = useState<IContrato | undefined>(undefined);

  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    CliCodigo: user.UsuClinica.toString(),
  };

  const informacoesPagina = `<b>Descrição:</b> Seleciona agemndamentos para o pagamento do médico. `;

  useEffect(() => {
    async function loadMedicos(): Promise<void> {
      await buscaOptionsMedico();
    }
    if (!optionsMedico || Object.keys(optionsMedico).length === 0)
      loadMedicos();
  }, [buscaOptionsMedico, optionsMedico]);

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.ATENDENTE) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);

      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().required(),
          ConUsuCodigo: Yup.string().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        const parametrosOpcionais = dados.ConUsuCodigo
          ? `&ConUsuCodigo=${dados.ConUsuCodigo}`
          : '';

        const response = await api.get<IRetornoContratoEAgendamentosMedico>(
          `/relatorios/procedimentosrealizadosmedico?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}&CliCodigo=${dados.CliCodigo}${parametrosOpcionais}`,
        );

        const dadosFormatados = response.data.agendamentos.map(rel => {
          return {
            ...rel,
            AgeInicioFormatado: format(
              parseISO(rel.AgeInicio.toString()),
              'dd/MM/yyyy',
            ),
            marcado: rel.podePagar,
          };
        });

        setContrato(
          response.data.contrato
            ? {
                ...response.data.contrato,
                ConValorFormatado: formatPrice(response.data.contrato.ConValor),
                ConValorExtraFormatado: formatPrice(
                  response.data.contrato.ConValorExtra,
                ),
                ConRemuneracao: response.data.contrato?.ConRemuneracao.replace(
                  /\[VALOR_CONTRATO]/g,
                  formatPrice(response.data.contrato.ConValor),
                )
                  .replace(
                    /\[VALOR_EXTENSO]/g,
                    numeroToExtenso(response.data.contrato.ConValor),
                  )
                  .replace(
                    /\[QUANTIDADE_ATENDIMENTO]/g,
                    response.data.contrato.ConQuantidade.toString(),
                  ),
              }
            : undefined,
        );

        const ordenado = arrayGroupBy(dadosFormatados, i => i.ConNome);

        const ordenadoFormatado = ordenado.map(ord => {
          return {
            grupo: ord[0].ConNome,
            totalRegistros: ord.length,
            dados: ord,
          };
        });

        setDadosRelatorio(ordenadoFormatado);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  const handleCheckChange = useCallback((ageCodigo: number) => {
    setDadosRelatorio(state => {
      if (state) {
        return state.map(st => {
          return {
            ...st,
            dados: st.dados.map(dt => {
              return {
                ...dt,
                marcado: dt.AgeCodigo === ageCodigo ? !dt.marcado : dt.marcado,
              };
            }),
          };
        });
      }
      return undefined;
    });
  }, []);

  const agendamentosSelecionados = useMemo(() => {
    const agendamentos =
      dadosRelatorio
        ?.flatMap(dados => dados.dados)
        .filter(dados => dados.marcado === true) || [];
    const codigos = agendamentos?.map(ag => ag.AgeCodigo);
    const totalPago =
      agendamentos?.reduce((tot, ag) => {
        return tot + ag.CprValorPag;
      }, 0) || 0;
    const totalCobrado =
      agendamentos?.reduce((tot, ag) => {
        return tot + ag.CprValor;
      }, 0) || 0;
    const quantidade = agendamentos?.length || 0;
    const nomeMedico =
      optionsMedico.find(
        op => Number(op.value) === agendamentos[0]?.ConUsuCodigo,
      )?.label || '';

    let valorAPagar = 0;
    switch (contrato?.ConTipo.toLowerCase()) {
      case 'por atendimento': {
        valorAPagar = quantidade * contrato.ConValor;
        break;
      }
      case 'por percentual': {
        valorAPagar = parseFloat(
          ((totalCobrado * contrato.ConValor) / 100).toFixed(2),
        );

        break;
      }
      case 'fechado com extra': {
        valorAPagar = parseFloat(contrato.ConValor.toFixed(2));
        if (quantidade > contrato.ConQuantidade)
          valorAPagar = parseFloat(
            (
              valorAPagar +
              (quantidade - contrato.ConQuantidade) * contrato.ConValorExtra
            ).toFixed(2),
          );

        break;
      }
      default: {
        valorAPagar = 0;
        break;
      }
    }
    return {
      codigos,
      totalPago,
      totalCobrado,
      quantidade,
      valorAPagar,
      nomeMedico,
      pagaRetorno: contrato?.ConPagaRetorno || false,
    };
  }, [dadosRelatorio, contrato, optionsMedico]);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      {showModalAddPagamento &&
        agendamentosSelecionados &&
        agendamentosSelecionados.codigos?.length > 0 &&
        contrato && (
          <Modal
            show={showModalAddPagamento}
            titulo="Adicionar Despesa Médica"
            handleFecharModal={() => setShowModalAddPagamento(false)}
          >
            <DespesaMedico
              despesa={agendamentosSelecionados}
              contrato={contrato}
              handleFecharModal={() => {
                setShowModalAddPagamento(false);
                formRefBusca.current?.submitForm();
              }}
            />
          </Modal>
        )}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2 d-flex justify-content-between">
            <h2 id="topoBusca">Lista de atendimento a pagar</h2>
            <PageInfo
              descricao={informacoesPagina}
              permissao={TIPO_USUARIO.ATENDENTE_CAIXA}
            />
          </div>
          {optionsClinica.length > 0 && user && (
            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={2}>
                  <Input name="dataInicio" label="De" type="date" required />
                </Col>
                <Col md={2}>
                  <Input name="dataFim" label="Até" type="date" required />
                </Col>
                <Col md={2}>
                  <Select
                    name="CliCodigo"
                    label="Clinica"
                    options={optionsClinica}
                    disabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
                  />
                </Col>

                <Col md={3}>
                  <SelectSync
                    name="ConUsuCodigo"
                    label="Médico"
                    options={optionsMedico}
                    handleSelectChange={handleOnChangeVazio}
                  />
                </Col>

                <Col md={2}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>
        <div className="bg-white border p-2 mb-2">
          <Row className="pl-2">
            <Col md={12} lg={12} className="mt-2">
              {contrato && (
                <table className="table border">
                  <tbody>
                    <tr>
                      <td colSpan={5}>
                        <h6>Dados Contrato</h6>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Tipo Contrato: <b>{contrato.ConTipo}</b>
                      </td>

                      <td>
                        Quantidade: <b>{contrato.ConQuantidade}</b>
                      </td>

                      <td>
                        Valor Contrato: <b>{contrato.ConValorFormatado}</b>
                      </td>
                      <td>
                        Valor Extra: <b>{contrato.ConValorExtraFormatado}</b>
                      </td>
                      <td>
                        Paga Retorno:{' '}
                        <b>{contrato.ConPagaRetorno ? 'Sim' : 'Não'}</b>
                      </td>
                    </tr>

                    <tr>
                      <td colSpan={5}>
                        <b>Remuneração:</b> {contrato?.ConRemuneracao}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
              {agendamentosSelecionados && contrato && (
                <>
                  <table className="table border">
                    <tbody>
                      <tr>
                        <td colSpan={4}>
                          <h6>
                            Resumo agendamentos selecionados para pagamento
                          </h6>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          Quantidade:{' '}
                          <b>{agendamentosSelecionados.quantidade}</b>
                        </td>

                        <td>
                          Valor cobrado:{' '}
                          <b>
                            {formatPrice(
                              agendamentosSelecionados?.totalCobrado || 0,
                            )}
                          </b>
                        </td>

                        <td>
                          Valor pago:{' '}
                          <b>
                            {formatPrice(
                              agendamentosSelecionados?.totalPago || 0,
                            )}
                          </b>
                        </td>
                        <td>
                          Valor à pagar:{' '}
                          <b>
                            {formatPrice(agendamentosSelecionados.valorAPagar)}
                          </b>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <Button
                    type="submit"
                    size="sm"
                    variant="outline-primary"
                    onClick={() => setShowModalAddPagamento(true)}
                  >
                    Adicionar Despesa
                  </Button>
                </>
              )}
              {!contrato && (
                <h3 className="mx-auto">Sem Contrato Cadastrado</h3>
              )}
            </Col>
          </Row>
        </div>
        <div className="bg-white border p-2">
          {dadosRelatorio && (
            <>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th style={{ width: '80px' }}>Data</th>
                    <th>Paciente</th>
                    <th>Procedimento</th>
                    <th>Valor Procedimentos</th>
                    <th>Valor Cobrado</th>
                    <th>Valor Pago</th>
                    <th>-</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.map(rel => (
                      <React.Fragment key={`des-${rel.grupo}`}>
                        <tr
                          style={{
                            fontSize: 'Small',
                            height: '30px',
                            fontWeight: 'bold',
                          }}
                        >
                          <td colSpan={2}>{rel.grupo}</td>
                          <td colSpan={5}>
                            Nº Registros: {rel.totalRegistros}
                          </td>
                        </tr>
                        {rel.dados.map(relatorio => (
                          <tr
                            key={`agenda-${relatorio.AgeCodigo}-${relatorio.Procedimento}`}
                            data-id={relatorio.AgeCodigo}
                            style={{ fontSize: '.7rem', height: '30px' }}
                          >
                            <td>{relatorio.AgeInicioFormatado}</td>
                            <td>{relatorio.PacNome}</td>
                            <td>{relatorio.Procedimento}</td>
                            <td>{relatorio.ProValor}</td>
                            <td>{relatorio.CprValor}</td>
                            <td>{relatorio.CprValorPag}</td>
                            <td>
                              <input
                                type="checkbox"
                                name={`chk-${relatorio.AgeCodigo}`}
                                checked={relatorio.marcado}
                                title={`${relatorio.AgeCodigo}`}
                                onChange={
                                  () => handleCheckChange(relatorio.AgeCodigo)
                                  // eslint-disable-next-line react/jsx-curly-newline
                                }
                                disabled={!relatorio.podePagar}
                              />
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </>
    </Container>
  );
};

export default PagamentoMedico;
